import { useTranslation } from 'react-i18next'
import { Button, Input } from '@arabquiz/ui'
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react'
import { ORDER_MAX_ITEMS, ORDER_MIN_ITEMS, type EditorQuestion } from './types'

type OrderItemsEditorProps = {
  question: Extract<EditorQuestion, { type: 'OrderSequence' }>
  onChange: (next: EditorQuestion) => void
}

/**
 * Order/Sequence editor. Items are listed in the **correct** order
 * (top → bottom = first → last). The host can reorder with the up/down
 * arrows, edit each label inline, and add/remove items within the
 * 2–8 range enforced by the server-side validator. The server will
 * shuffle the displayed sequence for each game so players never see
 * the canonical order. Extracted verbatim from `QuizEditor` (E1).
 */
export function OrderItemsEditor({ question, onChange }: OrderItemsEditorProps) {
  const { t } = useTranslation('quiz')

  function setItem(i: number, value: string) {
    onChange({
      ...question,
      items: question.items.map((item, j) => (i === j ? value : item)),
    })
  }

  function moveItem(i: number, direction: -1 | 1) {
    const target = i + direction
    if (target < 0 || target >= question.items.length) return
    const next = question.items.slice()
    const a = next[i]
    const b = next[target]
    if (a === undefined || b === undefined) return
    next[i] = b
    next[target] = a
    onChange({ ...question, items: next })
  }

  function removeItem(i: number) {
    if (question.items.length <= ORDER_MIN_ITEMS) return
    onChange({
      ...question,
      items: question.items.filter((_, j) => j !== i),
    })
  }

  function addItem() {
    if (question.items.length >= ORDER_MAX_ITEMS) return
    onChange({ ...question, items: [...question.items, ''] })
  }

  return (
    <fieldset>
      <legend className="block text-sm font-semibold text-brand-900 mb-2">
        {t('orderItemsLegend')}
      </legend>
      <p className="text-xs text-brand-600 mb-3 leading-relaxed">
        {t('orderItemsHint')}
      </p>
      <ol className="space-y-2">
        {question.items.map((item, i) => (
          <li
            key={`order-item-${i}`}
            className="flex items-center gap-2 rounded-xl border border-brand-200 bg-white ps-3 pe-2 py-2"
          >
            <span
              aria-hidden="true"
              className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700 font-bold font-display text-sm tabular-nums"
            >
              {i + 1}
            </span>
            <Input
              value={item}
              onChange={(e) => setItem(i, e.target.value)}
              placeholder={t('orderItemPlaceholder', { n: i + 1 })}
              maxLength={500}
              className="border-0 bg-transparent ps-1 pe-1 focus-visible:ring-0 focus-visible:ring-offset-0"
            />
            <button
              type="button"
              onClick={() => moveItem(i, -1)}
              disabled={i === 0}
              aria-label={t('moveUp')}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-brand-600 hover:bg-brand-50 hover:text-brand-800 disabled:opacity-30 disabled:hover:bg-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
            >
              <ArrowUp className="h-4 w-4" aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={() => moveItem(i, 1)}
              disabled={i === question.items.length - 1}
              aria-label={t('moveDown')}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-brand-600 hover:bg-brand-50 hover:text-brand-800 disabled:opacity-30 disabled:hover:bg-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
            >
              <ArrowDown className="h-4 w-4" aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={() => removeItem(i)}
              disabled={question.items.length <= ORDER_MIN_ITEMS}
              aria-label={t('deleteItem')}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-red-600 hover:bg-red-50 disabled:opacity-30 disabled:hover:bg-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
            >
              <Trash2 className="h-4 w-4" aria-hidden="true" />
            </button>
          </li>
        ))}
      </ol>
      {question.items.length < ORDER_MAX_ITEMS ? (
        <div className="mt-3">
          <Button
            type="button"
            variant="outline"
            onClick={addItem}
            className="gap-2"
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            <span>{t('addOrderItem')}</span>
          </Button>
        </div>
      ) : null}
    </fieldset>
  )
}
